import { useCallback, useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

export function usePdfDoc(url) {
  const [state, setState] = useState({ doc: null, error: false });
  useEffect(() => {
    let cancelled = false;
    setState({ doc: null, error: false });
    const task = pdfjs.getDocument(url);
    task.promise
      .then((doc) => { if (!cancelled) setState({ doc, error: false }); })
      .catch(() => { if (!cancelled) setState({ doc: null, error: true }); });
    return () => {
      cancelled = true;
      task.destroy();
    };
  }, [url]);
  return state;
}

export function usePageImages(doc, scale) {
  const [images, setImages] = useState({});
  const pending = useRef(new Set());
  const urls = useRef([]);

  useEffect(() => {
    setImages({});
    pending.current = new Set();
    return () => {
      urls.current.forEach((u) => URL.revokeObjectURL(u));
      urls.current = [];
    };
  }, [doc, scale]);

  const request = useCallback(async (n) => {
    if (!doc || n < 1 || n > doc.numPages || pending.current.has(n)) return;
    pending.current.add(n);
    try {
      const page = await doc.getPage(n);
      const vp = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = vp.width;
      canvas.height = vp.height;
      await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
      const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.9));
      const u = URL.createObjectURL(blob);
      urls.current.push(u);
      setImages((s) => ({ ...s, [n]: u }));
    } catch {
      pending.current.delete(n);
    }
  }, [doc, scale]);

  return [images, request];
}

export function useMediaQuery(query) {
  const [match, setMatch] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return match;
}
